import React from "react";
import { NumberInput, Stack, Text } from "@mantine/core";
import {
  CashDenominationKey,
  DENOMINATION_ORDER,
  DENOMINATION_VALUES,
  DenominationEntry,
} from "../../types/cashDrawer";
import {
  denominationsToTotal,
  formatCurrency,
} from "../../utils/cashDrawerUtils";
import styles from "./DenominationInput.module.css";

export interface DenominationInputProps {
  value: DenominationEntry[];
  onChange: (next: DenominationEntry[]) => void;
  disabled?: boolean;
  size?: "xs" | "sm" | "md";
  showTotal?: boolean;
  totalLabel?: string;
}

const BILLS: Array<{ key: CashDenominationKey; label: string }> = [
  { key: "hundred", label: "$100" },
  { key: "fifty", label: "$50" },
  { key: "twenty", label: "$20" },
  { key: "ten", label: "$10" },
  { key: "five", label: "$5" },
  { key: "two", label: "$2" },
  { key: "one", label: "$1" },
];

const COINS: Array<{ key: CashDenominationKey; label: string }> = [
  { key: "quarter", label: "Quarters" },
  { key: "dime", label: "Dimes" },
  { key: "nickel", label: "Nickels" },
  { key: "penny", label: "Pennies" },
];

const byKey = (entries: DenominationEntry[]) => {
  const map = new Map<CashDenominationKey, number>();
  entries.forEach((e) => map.set(e.denomination, e.count));
  return map;
};

const Row: React.FC<{
  label: string;
  denom: CashDenominationKey;
  count: number;
  disabled: boolean;
  size: "xs" | "sm" | "md";
  onChange: (count: number) => void;
}> = ({ label, denom, count, disabled, size, onChange }) => {
  const subtotal = count * DENOMINATION_VALUES[denom];
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowMultiplier}>x</span>
      <NumberInput
        size={size}
        min={0}
        step={1}
        value={count || ""}
        onChange={(v) => onChange(Number(v) || 0)}
        disabled={disabled}
        allowNegative={false}
        allowDecimal={false}
        hideControls
        placeholder="0"
        aria-label={`Count of ${label}`}
      />
      <span
        className={`${styles.rowSubtotal} ${
          count ? styles.rowSubtotalActive : ""
        }`}
      >
        {formatCurrency(subtotal)}
      </span>
    </div>
  );
};

export const DenominationInput: React.FC<DenominationInputProps> = ({
  value,
  onChange,
  disabled = false,
  size = "sm",
  showTotal = true,
  totalLabel = "Total",
}) => {
  const counts = byKey(value);
  const total = denominationsToTotal(
    DENOMINATION_ORDER.map((d) => ({
      denomination: d,
      count: counts.get(d) ?? 0,
    })),
  );

  const updateCount = (denomination: CashDenominationKey, count: number) => {
    const next = DENOMINATION_ORDER.map((d) => ({
      denomination: d,
      count:
        d === denomination
          ? Math.max(0, Math.floor(count || 0))
          : (counts.get(d) ?? 0),
    }));
    onChange(next);
  };

  return (
    <Stack gap="xs">
      <div className={styles.columns}>
        <div className={styles.column}>
          <div className={styles.columnLabel}>Bills</div>
          {BILLS.map(({ key, label }) => (
            <Row
              key={key}
              label={label}
              denom={key}
              count={counts.get(key) ?? 0}
              disabled={disabled}
              size={size}
              onChange={(c) => updateCount(key, c)}
            />
          ))}
        </div>
        <div className={styles.column}>
          <div className={styles.columnLabel}>Coins</div>
          {COINS.map(({ key, label }) => (
            <Row
              key={key}
              label={label}
              denom={key}
              count={counts.get(key) ?? 0}
              disabled={disabled}
              size={size}
              onChange={(c) => updateCount(key, c)}
            />
          ))}
        </div>
      </div>

      {showTotal && (
        <div className={styles.totalRow}>
          <Text fw={600}>{totalLabel}</Text>
          <Text fw={700} size="lg">
            {formatCurrency(total)}
          </Text>
        </div>
      )}
    </Stack>
  );
};

export default DenominationInput;
